import { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, Chip,
  Dialog, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { useTranslation } from 'react-i18next';
import { PmCard, PmInfoBox } from '../components/pm';
import { EnhancedDataTable, type ColumnDef } from '../components/common/EnhancedDataTable';
import { ListActionsColumn } from '../components/common/ListActionsColumn';
import { useNotifications } from '../context/NotificationContext';
import { formatApiError } from '../api/client';
import {
  useUsers, useCreateUser, useUpdateUser, useRevokePhqAccess,
  type AdminUser,
} from '../api/hooks/useUserAdmin';
import { AppDialogTitle } from '../components/common/AppDialogTitle';

interface Draft {
  id?: string;
  first_name: string;
  last_name: string;
  email_primary: string;
  status: string;
}

const EMPTY_DRAFT: Draft = { first_name: '', last_name: '', email_primary: '', status: 'active' };

/**
 * ISO-Zeitstempel als deutsches Datum mit Uhrzeit. Unbrauchbare Eingabe bleibt roh
 * stehen, damit ein Fehler im Dienst sichtbar wird statt als „—" zu verschwinden.
 */
function formatDateTimeDE(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function BenutzerverwaltungPage() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data: benutzerAntwort, isLoading } = useUsers();
  const users = benutzerAntwort?.items;
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const revokeAccess = useRevokePhqAccess();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const emailValid = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  const canSave = !!draft && draft.first_name.trim() && draft.last_name.trim() && emailValid(draft.email_primary);

  const saveDraft = async () => {
    if (!draft || !canSave) return;
    setSaving(true);
    try {
      if (draft.id) {
        await updateUser.mutateAsync({ id: draft.id, data: { first_name: draft.first_name.trim(), last_name: draft.last_name.trim(), status: draft.status } });
        notify(t('users.updated', 'Benutzer aktualisiert'), 'success');
      } else {
        const { created } = await createUser.mutateAsync({ first_name: draft.first_name.trim(), last_name: draft.last_name.trim(), email_primary: draft.email_primary.trim() });
        notify(
          created
            ? t('users.created', 'Benutzer angelegt — er meldet sich über „Passwort vergessen" an')
            : t('users.attached', 'Bestehender Mitarbeiter für Project HQ freigeschaltet — sein bisheriger Zugang bleibt unverändert'),
          'success',
        );
      }
      setDraft(null);
    } catch (err) {
      notify(formatApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // „Löschen" = PHQ-Zugang entziehen (mapping-only). Der Mensch, sein SMS-PRO-Zugang
  // und der Login bleiben bestehen — employee-Records sind PHQ<->SMS-PRO geteilt.
  const handleRevoke = async (row: AdminUser) => {
    try {
      await revokeAccess.mutateAsync(row.id);
      notify(t('users.accessRevoked', 'PHQ-Zugang entzogen — Mitarbeiter und SMS-PRO-Zugang bleiben bestehen'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const columns = useMemo<ColumnDef<AdminUser>[]>(() => [
    { id: 'name', header: t('users.name', 'Name'), accessor: (r) => r.display_name || `${r.first_name} ${r.last_name}`.trim(), sortable: true, filterable: true },
    { id: 'email', header: t('users.email', 'E-Mail'), accessor: 'email_primary', sortable: true, filterable: true },
    { id: 'personal_number', header: t('users.personalNumber', 'Personal-Nr.'), accessor: (r) => r.personal_number ?? '—', sortable: true },
    { id: 'status', header: tc('fields.status', 'Status'), accessor: 'status', sortable: true, filterable: true, filterType: 'select',
      render: (v) => <Chip label={String(v)} size="small" color={v === 'active' ? 'success' : 'default'} variant="outlined" /> },
    { id: 'frontends', header: t('users.access', 'Zugänge'), accessor: (r) => r.frontends.join(', '),
      render: (_v, r) => <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{r.frontends.map((f) => <Chip key={f} label={f} size="small" variant="outlined" />)}</Stack> },
    { id: 'aktivitaet', header: t('users.lastSeenPhq', 'Zuletzt in PHQ'), sortable: true,
      // Zeigt, wann dieser Mensch zuletzt IN PHQ war. Der Wert stammt aus PHQ selbst:
      // Beim Anmelden meldet die Oberfläche den Besuch an die Mitarbeiterverwaltung
      // (`api/anwesenheit.ts`), die ihn am Mitarbeitersatz führt.
      //
      // 🔴 Bewusst OHNE die Angaben des Identitätsdienstes, die hier bis 22.09.2026
      // als Zusatz standen. Sie beantworten eine andere Frage — „wann angemeldet",
      // nicht „wann in PHQ" — und sie sind auf STAGING dauerhaft leer: Der dortige
      // Verzeichnisbestand und die Mitarbeitersätze sind vollständig disjunkt, null
      // Überschneidung in beide Richtungen (Messung employee, 21.09.2026). Eine
      // Zusatzzeile, die immer „nicht zugeordnet" sagt, ist kein Hinweis, sondern
      // Rauschen unter der Angabe, auf die es ankommt. Owner-Entscheid 22.09.2026.
      //
      // Auf PROD kennt das Verzeichnis 880 Konten; dort könnten die Angaben wieder
      // Sinn ergeben — dann aber als bewusste Entscheidung, nicht als Überbleibsel.
      accessor: (r) => r.last_seen_phq_at ?? '',
      render: (_v, r) => (
        <Stack spacing={0} sx={{ lineHeight: 1.2 }}>
          <Typography variant="body2">
            {r.last_seen_phq_at ? formatDateTimeDE(r.last_seen_phq_at) : '—'}
          </Typography>
        </Stack>
      ) },
    { id: 'actions', header: '', accessor: () => null, hideable: false,
      render: (_v, r) => (
        <ListActionsColumn
          row={r}
          onEdit={(row) => setDraft({ id: row.id, first_name: row.first_name, last_name: row.last_name, email_primary: row.email_primary, status: row.status })}
          onDelete={(row) => handleRevoke(row)}
          deleteTitle={t('users.revokeTitle', 'PHQ-Zugang entziehen?')}
          deleteMessage={t('users.revokeMessage', 'Der Benutzer verliert den Zugang zu Project HQ. Die Mitarbeiter-Stammdaten und ein eventuell vorhandener SMS-PRO-Zugang bleiben unberührt. Der Zugang lässt sich jederzeit durch erneutes Anlegen wieder gewähren.')}
        />
      ) },
  ], [t, tc]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('users.title', 'Benutzerverwaltung')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('users.subtitle', 'Benutzer für Project HQ anlegen, bearbeiten und den Zugang entziehen. Alle Benutzer haben vollen Zugriff.')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setDraft({ ...EMPTY_DRAFT })}>
          {t('users.add', 'Benutzer anlegen')}
        </Button>
      </Stack>

      <PmCard>
        <EnhancedDataTable
          tableId="benutzerverwaltung"
          columns={columns}
          data={users ?? []}
          loading={isLoading}
          getRowId={(r) => r.id}
          exportFilename="benutzer"
          emptyState={{ title: t('users.empty', 'Noch keine Benutzer'), hint: t('users.emptyHint', 'Lege den ersten Benutzer an.') }}
        />
      </PmCard>

      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{draft?.id ? t('users.editTitle', 'Benutzer bearbeiten') : t('users.add', 'Benutzer anlegen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!draft?.id && (
              <PmInfoBox variant="info">
                {t('users.mailHint', 'Es wird KEINE E-Mail verschickt. Ein neuer Benutzer meldet sich über „Passwort vergessen" auf der Login-Seite an und setzt sein Passwort selbst. Ist die E-Mail bereits bekannt, wird der bestehende Mitarbeiter nur für Project HQ freigeschaltet — sein Zugang bleibt unverändert.')}
              </PmInfoBox>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label={t('users.firstName', 'Vorname')} value={draft?.first_name ?? ''} fullWidth autoFocus
                onChange={(e) => setDraft((d) => d && { ...d, first_name: e.target.value })}
              />
              <TextField
                label={t('users.lastName', 'Nachname')} value={draft?.last_name ?? ''} fullWidth
                onChange={(e) => setDraft((d) => d && { ...d, last_name: e.target.value })}
              />
            </Stack>
            <TextField
              label={t('users.email', 'E-Mail')} type="email" value={draft?.email_primary ?? ''} fullWidth
              disabled={!!draft?.id}
              helperText={draft?.id ? t('users.emailLocked', 'E-Mail ist die Login-Kennung und nicht änderbar.') : undefined}
              error={!!draft && draft.email_primary.length > 0 && !emailValid(draft.email_primary)}
              onChange={(e) => setDraft((d) => d && { ...d, email_primary: e.target.value })}
            />
            {draft?.id && (
              <TextField
                select label={tc('fields.status', 'Status')} value={draft.status} fullWidth
                onChange={(e) => setDraft((d) => d && { ...d, status: e.target.value })}
              >
                <MenuItem value="active">{t('users.statusActive', 'Aktiv')}</MenuItem>
                <MenuItem value="suspended">{t('users.statusSuspended', 'Gesperrt')}</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" disabled={!canSave || saving} onClick={saveDraft}>{tc('actions.save', 'Speichern')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
